import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  CargoType,
  DeliveryPayer,
  MeasurementsUnit,
  PaymentType,
} from '@international-invoice';
import {
  PayerFeesCustomsDictionary,
  EInvoiceInformationControlNames,
} from 'app/components/invoice-form/containers/international-invoice/components/invoice-information/invoice-information.constants';
import { AddressTypes, IListData } from '@models';
import {
  CreateInternationalInvoiceResponse,
  CreateInternationalInvoiceTemplateResponse,
  DeliveryType,
  GetCitiesData,
  GetCountriesData,
  GetCurrenciesData,
  GetCurrencyRatesData,
  GetDocumentPriceByCounterpartyTypeData,
  GetDocumentPriceByCounterpartyTypePayload,
  GetGoodsClassifierData,
  GetIncotermsData,
  GetWarehousesData,
  InternationalInvoiceTranslitDictionary,
  InvoiceForApi,
  transliterate,
  UpdateInternationalInvoiceResponse,
  WarehouseCategory,
} from '@shared';
import * as moment from 'moment';
import { BehaviorSubject, map, Observable, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { InvoicesTemplateFilters } from '../components/dashboard/invoices/components/invoices-template/invoices-template.models';
import {
  padCodeUKTZEDDestByCountry,
  trimCodeUKTZEDByRecipientCountry,
} from '../components/invoice-form/containers/international-invoice/components/parcel-places/ii-code-uktzed/ii-code-uktzed.utils';
import { getGoodsClassifierDescription } from '../components/invoice-form/containers/international-invoice/components/parcel-places/ii-place-description/ii-place-description.utils';
import { EParcelPlacesFormControlNames } from '../components/invoice-form/containers/international-invoice/components/parcel-places/parcel-places.enums';
import { normalizeCountry } from '../components/invoice-form/containers/international-invoice/countries/country.utils';
import {
  IITemplate,
  IITemplateListItem,
} from '../components/invoice-form/containers/international-invoice/international-invoice.models';
import { Invoice } from '../models/invoice/invoice.model';
import { ApiMethods } from './api/apiMethods';
import { ConfigService } from './config.service';
import { NoRestApiHelper } from './no-rest-api.helper';
import { UserTypeService } from './user-type.service';
import {
  InternationalInvoice,
  IInternationalInvoicePlace,
  IInternationalInvoiceRaw,
  IInvoiceRawProperties,
} from '../models/international-invoice';
import { ApiListResponse } from '../models/api-list-response.model';
import { copyProps } from './invoices/helpers/bind-list-to-model';

@Injectable({
  providedIn: 'root',
})
export class InternationalInvApiService {
  private toggle = new BehaviorSubject<boolean>(false);

  constructor(
    private http: HttpClient,
    private configService: ConfigService,
    private noRestApiHelper: NoRestApiHelper,
    private userTypeService: UserTypeService,
  ) {
  }

  setToggle(val: boolean) {
    this.toggle.next(val);
  }

  saveContact(contact): Observable<any> {
    const body = {
      system: this.configService.get('system'),
      modelName: 'InternationalGeneral',
      calledMethod: 'saveRecipient',
      methodProperties: this._contactToMethodParams(contact),
    };
    return this.http.post(`${this.configService.get('apiUrl')}json/`, body).pipe(
      map((data) => this.noRestApiHelper.checkErrors({ data })),
      map((response) => response.data.data),
    );
  }

  getCities(countryRef: string, name: string): Observable<GetCitiesData[]> {
    const body = {
      system: this.configService.get('system'),
      modelName: 'International',
      calledMethod: 'getCities',
      methodProperties: {
        Country: countryRef,
        FindByString: name,
        Page: '1',
        Limit: '200',
      },
    };

    return this.http.post(`${this.configService.get('apiUrl')}json/`, body).pipe(
      map((data) => this.noRestApiHelper.checkErrors({ data })),
      map((response) => response.data.data),
    );
  }

  getGoodsClassifier(
    query: string,
    CountryCodeDest: string,
    CountryCodeDept: string = 'UA',
    page = 1,
    limit = 15,
  ): Observable<GetGoodsClassifierData[]> {
    const body = {
      system: this.configService.get('system'),
      modelName: 'International',
      calledMethod: 'getGoodsClassifierV2',
      methodProperties: {
        FindByString: query,
        CountryCodeDept,
        CountryCodeDest,
        Page: page,
        Limit: limit,
      },
    };

    return this.http.post(`${this.configService.get('apiUrl')}json/`, body).pipe(
      map((data) => this.noRestApiHelper.checkErrors({ data })),
      map((response) => response.data.data),
    );
  }

  getWarehouses(
    countryRef: string,
    cityName: string,
    warehouseCategory: WarehouseCategory,
    CounterpartyRef?: string,
  ): Observable<GetWarehousesData[]> {
    const body = {
      system: this.configService.get('system'),
      modelName: 'International',
      calledMethod: 'getWarehouses',
      methodProperties: {
        Country: countryRef,
        City: cityName,
        WarehouseCategory: warehouseCategory,
        ...(CounterpartyRef ? { CounterpartyRef } : {}),
      },
    };

    return this.http.post(`${this.configService.get('apiUrl')}json/`, body).pipe(
      map((data) => this.noRestApiHelper.checkErrors({ data })),
      map((response) => response.data.data),
    );
  }

  searchRecipientByPhone(phone: string): Observable<any> {
    const body = {
      system: this.configService.get('system'),
      modelName: 'InternationalGeneral',
      calledMethod: 'getRecipients',
      methodProperties: {
        Phone: phone,
      },
    };
    return this.http.post(`${this.configService.get('apiUrl')}json/`, body).pipe(
      map((data) => this.noRestApiHelper.checkErrors({ data })),
      map((response) => response.data.data),
    );
  }

  getRecipientByRef(ref: string): Observable<any> {
    const body = {
      system: this.configService.get('system'),
      modelName: 'InternationalGeneral',
      calledMethod: 'getRecipients',
      methodProperties: {
        Ref: ref,
      },
    };
    return this.http.post(`${this.configService.get('apiUrl')}json/`, body).pipe(
      map((data) => this.noRestApiHelper.checkErrors({ data })),
      map((response) => response.data.data),
    );
  }

  getCountries(ref?): Observable<GetCountriesData[]> {
    const body = {
      system: this.configService.get('system'),
      modelName: 'International',
      calledMethod: 'getCountries',
      methodProperties: {
        Ref: ref,
        Limit: 300,
      },
    };
    return this.http.post(`${this.configService.get('apiUrl')}json/`, body).pipe(
      map((data) => this.noRestApiHelper.checkErrors({ data })),
      map((response) => this.filterCountries(response.data.data)),
      map((countries) => this.normalizeCountries(countries)),
    );
  }

  getUSStates(searchBy?: 'Code' | 'FindByString', query?: string) {
    const body = {
      modelName: 'InternationalGeneral',
      calledMethod: 'getStatesOfAmerica',
      methodProperties: searchBy ? { [searchBy]: query } : {},
    };
    return this.http.post(`${this.configService.get('apiUrl')}json/`, body).pipe(
      map((data) => this.noRestApiHelper.checkErrors({ data })),
      map((response) => response.data.data),
    );
  }

  getProvinciesOfCanada(searchBy?: 'Code' | 'FindByString', query?: string) {
    const body = {
      modelName: 'InternationalGeneral',
      calledMethod: 'getProvinciesOfCanada',
      methodProperties: searchBy ? { [searchBy]: query } : {},
    };
    return this.http.post(`${this.configService.get('apiUrl')}json/`, body).pipe(
      map((data) => this.noRestApiHelper.checkErrors({ data })),
      map((response) => response.data.data),
    );
  }

  getIncoterms(ref?: string): Observable<GetIncotermsData[]> {
    const body = {
      system: this.configService.get('system'),
      modelName: 'InternationalGeneral',
      calledMethod: 'getIncoterms',
      methodProperties: {
        Limit: 300,
        Ref: ref,
      },
    };
    return this.http.post(`${this.configService.get('apiUrl')}json/`, body).pipe(
      map((data) => this.noRestApiHelper.checkErrors({ data })),
      map((response) => response.data.data),
    );
  }

  getCurrency(ref?: string): Observable<GetCurrenciesData[]> {
    const body = {
      system: this.configService.get('system'),
      modelName: 'InternationalGeneral',
      calledMethod: 'getCurrencies',
      methodProperties: {
        Limit: 1000,
        Ref: ref,
      },
    };

    return this.http.post(`${this.configService.get('apiUrl')}json/`, body).pipe(
      map((data) => this.noRestApiHelper.checkErrors({ data })),
      map((response) => response.data.data),
    );
  }

  getUnits(ref?: string): Observable<MeasurementsUnit[]> {
    const body = {
      system: this.configService.get('system'),
      modelName: 'InternationalGeneral',
      calledMethod: 'getUnits',
      methodProperties: {
        Limit: 300,
        Ref: ref,
      },
    };
    return this.http.post(`${this.configService.get('apiUrl')}json/`, body).pipe(
      map((data) => this.noRestApiHelper.checkErrors({ data })),
      map((response) => response.data.data),
    );
  }

  getExportTarget(ref?): Observable<any> {
    const body = {
      system: this.configService.get('system'),
      modelName: 'InternationalGeneral',
      calledMethod: 'getExportReasons',
      methodProperties: {
        Limit: 100,
        Ref: ref,
      },
    };
    return this.http.post(`${this.configService.get('apiUrl')}json/`, body).pipe(
      map((data) => this.noRestApiHelper.checkErrors({ data })),
      map((response) => response.data.data),
    );
  }

  getCargoTypes(ref?: string): Observable<CargoType[]> {
    const body = {
      system: this.configService.get('system'),
      modelName: 'InternationalGeneral',
      calledMethod: 'getShipmentTypes',
      methodProperties: {
        Ref: ref,
      },
    };
    return this.http.post(`${this.configService.get('apiUrl')}json/`, body).pipe(
      map((data) => this.noRestApiHelper.checkErrors({ data })),
      map((response) => response.data.data),
    );
  }

  getDeliveryTypes(ref?): Observable<DeliveryType[]> {
    const body = {
      system: this.configService.get('system'),
      modelName: 'InternationalGeneral',
      calledMethod: 'getDeliveryTypes',
      methodProperties: {
        Ref: ref,
      },
    };
    return this.http.post(`${this.configService.get('apiUrl')}json/`, body).pipe(
      map((data) => this.noRestApiHelper.checkErrors({ data })),
      map((response) => response.data.data),
    );
  }

  getIITemplate(Ref: string): Observable<IITemplate> {
    const body = {
      system: this.configService.get('system'),
      modelName: 'InternationalTemplateGeneral',
      calledMethod: 'getDocument',
      methodProperties: { Ref },
    };
    return this.http.post(`${this.configService.get('apiUrl')}json/`, body).pipe(
      map((data) => this.noRestApiHelper.checkErrors({ data })),
      map((response) => response.data.data[0]),
    );
  }

  getIITemplates(query?: string, Ref?: string): Observable<IITemplateListItem[]> {
    const body = {
      system: this.configService.get('system'),
      modelName: 'InternationalTemplateGeneral',
      calledMethod: 'getDocumentList',
      methodProperties: {
        State: 'Order',
        Page: 1,
        Limit: 200,
        ...(query ? { FindByString: query } : {}),
        ...(Ref ? { Ref } : {}),
      },
    };
    return this.http.post(`${this.configService.get('apiUrl')}json/`, body).pipe(
      map((data) => this.noRestApiHelper.checkErrors({ data })),
      map((response) => response.data.data),
      catchError(() => of([])),
    );
  }

  getInvoice(ref: string): Observable<any> {
    const body = {
      system: this.configService.get('system'),
      modelName: 'InternationalGeneral',
      calledMethod: 'getDocument',
      methodProperties: {
        Ref: ref,
      },
    };
    return this.http.post(`${this.configService.get('apiUrl')}json/`, body).pipe(
      map((data) => this.noRestApiHelper.checkErrors({ data })),
      map((response) => response?.data?.data?.[0]),
    );
  }

  updateInvoice(invoice): Observable<UpdateInternationalInvoiceResponse> {
    const body = {
      system: this.configService.get('system'),
      modelName: 'InternationalGeneral',
      calledMethod: 'update',
      methodProperties: this.mapFormToApi(invoice),
    };
    return this.http.post(`${this.configService.get('apiUrl')}json/`, body).pipe(
      map((data) => this.noRestApiHelper.checkErrors({ data })),
      map((response) => response.data.data[0]),
    );
  }

  deleteInvoice(ref): Observable<any> {
    const body = {
      system: this.configService.get('system'),
      modelName: 'InternationalGeneral',
      calledMethod: 'delete',
      methodProperties: {
        Ref: ref,
      },
    };
    return this.http.post(`${this.configService.get('apiUrl')}json/`, body).pipe(
      map((data) => this.noRestApiHelper.checkErrors({ data })),
      map((response) => response.data.data),
    );
  }

  getRecipientWarhouses(code: string, warehouse?: string): Observable<any> {
    const body = {
      system: 'MobileApp',
      modelName: 'International',
      calledMethod: ApiMethods.getWarehouses,
      methodProperties: {
        WarehouseNumber: warehouse,
        CountryCode: code,
      },
    };
    return this.http.post(`${this.configService.get('apiUrl')}json/`, body).pipe(
      map((data) => this.noRestApiHelper.checkErrors({ data })),
      map((response) => response.data.data),
    );
  }

  getInvoicesList(state: string): Observable<any> {
    const body = {
      system: this.configService.get('system'),
      modelName: 'InternationalGeneral',
      calledMethod: 'getDocumentList',
      methodProperties: {
        GetFullList: 1,
        State: state,
      },
    };
    return this.http.post(`${this.configService.get('apiUrl')}json/`, body).pipe(
      map((data) => this.noRestApiHelper.checkErrors({ data })),
      map((response) => this.bindListToModel(response)),
    );
  }

  getIntTemplatesList(pageIndex: number, limit: number, filters: InvoicesTemplateFilters): Observable<IListData<Invoice>> {
    const body = {
      system: this.configService.get('system'),
      modelName: 'InternationalTemplateGeneral',
      calledMethod: 'getDocumentList',
      methodProperties: {
        State: 'Order',
        Page: pageIndex,
        Limit: limit,
        ...filters,
      },
    };
    return this.http.post(`${this.configService.get('apiUrl')}json/`, body).pipe(
      map((data) => this.noRestApiHelper.checkErrors({ data })),
      map((response) => this.bindListToModelWithPagination(response)),
    );
  }

  bindListToModelWithPagination(response: ApiListResponse<Invoice>): IListData<Invoice> {
    return {
      list: response.data.data,
      total: response.data.info.totalCount,
    };
  }

  bindListToModel(response: any): IListData<InternationalInvoice> {
    return {
      list: this.mapIIToModel(response.data.data),
      total: response.data.info.totalCount,
    };
  }

  private mapIIToModel(invoices: IInternationalInvoiceRaw[]): InternationalInvoice[] {
    try {
      return invoices.map((el) => {
        const invoice = new InternationalInvoice();
        copyProps(el, invoice, IInvoiceRawProperties);
        invoice.Weight = el.Places.reduce((acc, place: IInternationalInvoicePlace) => acc + place.ActualWeight, 0);
        invoice.VolumetricWeight  = el.Places.reduce((acc, place: IInternationalInvoicePlace ) => acc + place.VolumetricWeight, 0);
        return invoice;
      })
    } catch (e){
      console.warn('Error in mapIItoModel', e);
      return [];
    }
  }

  translateText(text: string): Observable<any> {
    const body = {
      system: this.configService.get('system'),
      modelName: 'CommonGeneral',
      calledMethod: 'translateText',
      methodProperties: {
        Text: text,
      },
    };
    return this.http.post(`${this.configService.get('apiUrl')}json/`, body).pipe(
      map((data) => this.noRestApiHelper.checkErrors({ data })),
      map((response) => response.data.data),
      map((data) => data[0]),
      catchError(() => {
        console.error(`Translation error for text: ${text}`);
        return of(null);
      }),
    );
  }

  getCurrencyRates(code?: string): Observable<GetCurrencyRatesData[]> {
    const body = {
      system: this.configService.get('system'),
      modelName: 'Common',
      calledMethod: 'getCurrencyRates',
      methodProperties: {
        Date: moment().format('DD.MM.YYYY'),
        Code: code,
      },
    };

    return this.http.post(`${this.configService.get('apiUrl')}json/`, body).pipe(
      map((data) => this.noRestApiHelper.checkErrors({ data })),
      map((response) => response.data.data),
    );
  }

  getDocumentPrice(form): Observable<any> {
    const body = {
      system: this.configService.get('system'),
      modelName: 'InternationalGeneral',
      calledMethod: 'getDocumentPrice',
      methodProperties: this._priceToMethodParams(form),
    };
    return this.http.post(`${this.configService.get('apiUrl')}json/`, body).pipe(
      map((data) => this.noRestApiHelper.checkErrors({ data })),
      map((response) => response.data.data),
    );
  }

  getDocumentPriceByCounterpartyType(payload: GetDocumentPriceByCounterpartyTypePayload): Observable<GetDocumentPriceByCounterpartyTypeData> {
    const body = {
      system: this.configService.get('system'),
      modelName: 'International',
      calledMethod: 'getDocumentPriceByCounterpartyType',
      methodProperties: payload,
    };
    return this.http.post(`${this.configService.get('apiUrl')}json/`, body)
      .pipe(
        map((data) => this.noRestApiHelper.checkErrors({ data })),
        map((response) => response.data.data[0]),
      );
  }

  createIntInvoice(invoice): Observable<CreateInternationalInvoiceResponse> {
    const body = {
      system: this.configService.get('system'),
      modelName: 'InternationalGeneral',
      calledMethod: 'save',
      methodProperties: this.mapFormToApi(invoice),
    };
    return this.http.post(`${this.configService.get('apiUrl')}json/`, body).pipe(
      map((data) => this.noRestApiHelper.checkErrors({ data })),
      map((response) => response.data.data[0]),
    );
  }

  createIntInvoiceTemplate(invoice): Observable<CreateInternationalInvoiceTemplateResponse> {
    const body = {
      system: this.configService.get('system'),
      modelName: 'InternationalTemplateGeneral',
      calledMethod: 'save',
      methodProperties: this.mapFormToApi(invoice),
    };
    return this.http.post(`${this.configService.get('apiUrl')}json/`, body).pipe(
      map((data) => this.noRestApiHelper.checkErrors({ data })),
      map((response) => response.data.data[0]),
    );
  }

  getPayerTypes(ref?: string): Observable<DeliveryPayer[]> {
    const body = {
      system: this.configService.get('system'),
      modelName: 'InternationalGeneral',
      calledMethod: 'getPayerTypes',
      methodProperties: {
        Ref: ref,
      },
    };
    return this.http.post(`${this.configService.get('apiUrl')}json/`, body).pipe(
      map((data) => this.noRestApiHelper.checkErrors({ data })),
      map((response) => response.data.data),
    );
  }

  delete(ref?): Observable<any> {
    const body = {
      system: this.configService.get('system'),
      modelName: 'InternationalGeneral',
      calledMethod: 'deleteRecipient',
      methodProperties: {
        Ref: ref,
      },
    };
    return this.http.post(`${this.configService.get('apiUrl')}json/`, body).pipe(
      map((data) => this.noRestApiHelper.checkErrors({ data })),
      map((response) => response.data.data),
    );
  }

  getPaymentTypes(ref?: string): Observable<PaymentType[]> {
    const body = {
      system: this.configService.get('system'),
      modelName: 'InternationalGeneral',
      calledMethod: 'getPaymentTypes',
      methodProperties: {
        Ref: ref,
      },
    };
    return this.http.post(`${this.configService.get('apiUrl')}json/`, body).pipe(
      map((data) => this.noRestApiHelper.checkErrors({ data })),
      map((response) => response.data.data),
    );
  }

  getPictures(ref) {
    const body = {
      system: this.configService.get('system'),
      modelName: 'International',
      calledMethod: 'getFileForExpressWaybill',
      methodProperties: {
        DocumentRef: ref,
      },
    };
    return this.http.post(`${this.configService.get('apiUrl')}json/`, body).pipe(
      map((data) => this.noRestApiHelper.checkErrors({ data })),
      map((response) => response.data.data),
    );
  }

  public _contactToMethodParams(contact): any {
    const methodParams: any = {};
    methodParams.Phone = contact.RecipientContacts.phone;
    methodParams.Type = contact.RecipientCounterpartyType;
    methodParams.Email = contact.RecipientContacts.email;
    methodParams.ContactPerson = contact.translits.recipientFirstName + ' ' + contact.translits.recipientLastName;
    methodParams.ZipCode = contact.AddressRecipient.index;
    methodParams.CountryRef = contact.AddressRecipient.country.Ref;
    methodParams.CityDescription = contact.translits.recipientCity;
    const state = contact.AddressRecipient.state ? `${contact.AddressRecipient.state.Code}; ` : '';
    methodParams.AddressDescription =
      state + contact.translits.recipientStreet + '; ' + contact.translits.recipientBuilding + '; ';
    if (contact.RecipientCounterpartyType === 'Organization') {
      methodParams.Description = contact.translits.organization;
    } else {
      methodParams.Description = 'Private Person';
    }
    return methodParams;
  }

  translit(text: string): string {
    return transliterate(text, InternationalInvoiceTranslitDictionary);
  }

  getSenderAddress(invoice): string {
    return invoice.NewSenderAddress?.Ref || invoice.SenderAddress.data.Ref || invoice.SenderAddress.data.warehouse.Ref;
  }

  getSenderRegistrationAddress(invoice): string {
    return invoice.NewSenderRegistrationAddress?.Ref || invoice.SenderRegistrationAddress?.data.Ref;
  }

  private filterCountries(countries: GetCountriesData[]): GetCountriesData[] {
    return countries.filter((c) => c.Code !== 'RU').filter((c) => c.Code !== 'BY');
  }

  private normalizeCountries(countries: GetCountriesData[]): GetCountriesData[] {
    return countries.map(normalizeCountry);
  }

  private _priceToMethodParams(post): any {
    const methodParams: any = {};
    methodParams.CounterpartySender = post.ContactSender.data.CounterpartyRef ?? post.ContactSenderOrg.Ref;
    methodParams.PickupCountry = 'UA';
    methodParams.DeliveryCountry = post.AddressRecipient.country.Code;
    methodParams.Promocode = '';

    const addressType = post.AddressRecipient.Address.type;
    const senderAddressType = post.SenderAddress.type;

    if (addressType === AddressTypes.WAREHOUSE || addressType === AddressTypes.POSTBOX) {
      methodParams.ServiceType = `${senderAddressType}Warehouse`;
    } else {
      methodParams.ServiceType = `${senderAddressType}Doors`;
    }

    methodParams.DeliveryType = post.type.DeliveryType;
    methodParams.ShipmentType = post.CargoType.Ref;
    methodParams.Parcels = [];
    post.OptionsSeat.forEach((option) => {
      methodParams.Parcels.push({
        Length: option.volumetricLength,
        Height: option.volumetricHeight,
        Width: option.volumetricWidth,
        ActualWeight: option.weight,
      });
    });
    return methodParams;
  }

  private mapFormToApi(invoice: InvoiceForApi): any {
    try {
      if (!invoice.translatedItems) {
        console.warn('No translated items in mapFormToApi');
      }

      const methodParams: any = {};
      if (invoice.ref) {
        methodParams.Ref = invoice.ref;
      }

      if (invoice.Sender) {
        methodParams.CounterpartySender = invoice.Sender.CounterpartyRef;
        methodParams.CounterpartySenderTranslit = invoice.editTranslit.translateCounterpartyName || '';
        methodParams.ContactPersonSender = invoice.Sender.Ref;
        methodParams.ContactPersonSenderTranslit = invoice.editTranslit.editedName ?? invoice.translits?.senderName;
        methodParams.PhoneSender = invoice.Sender.Phones;
      } else {
        methodParams.CounterpartySender = invoice.ContactSender.data.CounterpartyRef;
        methodParams.CounterpartySenderTranslit = invoice.editTranslit.translateCounterpartyName || '';
        methodParams.ContactPersonSender = invoice.ContactSender.data.Ref;
        methodParams.ContactPersonSenderTranslit = invoice.editTranslit.editedName ?? invoice.translits?.senderName;
        methodParams.PhoneSender = invoice.ContactSender.data.Phones;
      }

      methodParams.EmailSender = invoice.SenderEmail;
      if (invoice.SenderIOSS) {
        methodParams.IOSSNumber = invoice.SenderIOSS;
      }

      if (invoice.EoriCodeSender) {
        methodParams.EoriCodeSender = invoice.EoriCodeSender;
      }

      if (invoice.OrderNumber) {
        methodParams.OrderNumber = invoice.OrderNumber;
      }

      if (invoice.EoriCodeRecipient) {
        methodParams.EoriCodeRecipient = invoice.EoriCodeRecipient;
      }

      // region sender address
      methodParams.SenderAddressRef = this.getSenderAddress(invoice);
      if (this.getSenderRegistrationAddress(invoice)) {
        methodParams.SenderAddressRegistrationRef = this.getSenderRegistrationAddress(invoice);
      }
      // endregion sender address

      methodParams.DeliveryType = invoice.DeliveryTypes.type.DeliveryType || invoice.DeliveryTypes.type.Ref;

      // region recipient
      if (invoice.RecipientContacts.recipientRef && invoice.recipientRef === undefined) {
        methodParams.CounterpartyRecipientRef = invoice.RecipientContacts.recipientRef;
      } else {
        methodParams.CounterpartyRecipientRef = invoice.recipientRef;
      }

      if (invoice.AddressRecipient.country.RecipientRegistrationAddressRequired) {
        if (invoice.AddressRecipient.Address.type === AddressTypes.DOORS) {
          methodParams.RegistrationAddressRecipient = [
            {
              State: '',
              ZipCode: invoice.AddressRecipient.index,
              City: invoice.translits.recipientCity,
              Street: invoice.translits.recipientStreet,
              Building: invoice.translits.recipientBuilding,
              Apartment: invoice.translits.recipientRoom,
            },
          ];
        }
      }

      methodParams.CounterpartyRecipientLastName = invoice.translits?.recipientLastName;
      methodParams.CounterpartyRecipientFirstName = invoice.translits.recipientFirstName;

      // Phone and email
      methodParams.PhoneRecipient = invoice.RecipientContacts.phone.replace(/\D/gi, '');
      methodParams.EmailRecipient = invoice.RecipientContacts.email;

      methodParams.CompanyTINRecipient = invoice.RecipientInfo.RecipientNIP;

      methodParams.CounterpartyTypeRecipient = invoice.RecipientCounterpartyType;

      if (invoice.RecipientCounterpartyType === 'Organization') {
        methodParams.CounterpartyRecipient = invoice.translits.organization;
      }

      // region recipientAddress
      if (invoice.AddressRecipient.state) {
        methodParams.RegionRecipientCode = invoice.AddressRecipient.state.Code;
      }

      // region CASE DOORS
      if (invoice.AddressRecipient.Address.type === AddressTypes.DOORS) {
        methodParams.CountryRecipient = invoice.AddressRecipient.country.Ref;
        methodParams.ZipCodeRecipient = invoice.AddressRecipient.index;
        methodParams.CityRecipient = invoice.translits.recipientCity;
        methodParams.CounterpartyRecipientStreet = invoice.translits.recipientStreet;
        methodParams.CounterpartyRecipientHouse = invoice.translits.recipientBuilding;
        methodParams.CounterpartyRecipientApart = invoice.translits.recipientRoom;
        methodParams.AdditionalInfoRecipient = invoice.translits.recipientInfo;
      }
      // endregion CASE DOORS

      // region CASE WAREHOUSE
      if (invoice.AddressRecipient.Address.type === AddressTypes.WAREHOUSE) {
        methodParams.CountryRecipient = invoice.AddressRecipient.country.Ref;
        methodParams.CityRecipient = invoice.translits.recipientCity;
        methodParams.WarehouseRecipient = invoice.AddressRecipient.Address.data.warehouse.WarehouseRef;
      }
      // endregion CASE WAREHOUSE

      // region CASE POSTBOX
      if (invoice.AddressRecipient.Address.type === AddressTypes.POSTBOX) {
        methodParams.CountryRecipient = invoice.AddressRecipient.country.Ref;
        methodParams.CityRecipient = invoice.translits.recipientCity;
        methodParams.WarehouseRecipient = invoice.AddressRecipient.Address.data.postbox.WarehouseRef;
      }
      // endregion CASE POSTBOX

      // if (invoice.DeliveryTypes.warehouse) {
      //   methodParams.WarehouseRecipient =
      //     invoice.DeliveryTypes.warehouse.WarehouseRef;
      // } else {
      //   methodParams.WarehouseRecipient = '';
      // }
      // endregion recipientAddress
      // endregion recipient

      methodParams.TypeOfShipment = invoice.CargoType.Ref;
      methodParams.CargoDescription = invoice.translits.parcelDescription || invoice.ParcelDescription;
      methodParams.PlacesAmount = invoice.OptionsSeat.length;
      methodParams.ExportReason = invoice.ExportTarget.target.Ref;
      if (invoice.ExportTarget.additionalInfo) {
        methodParams.AdditionalInformationExportReason = invoice.additionalExportInfo.additionalInfoTranslit;
      } else {
        methodParams.AdditionalInformationExportReason = '';
      }
      methodParams.PayerType = invoice.Payer.Ref;
      methodParams.PaymentMethod = invoice.PaymentType.Ref;

      if (invoice.InvoiceInformation[EInvoiceInformationControlNames.customsFeePayer]) {
        methodParams.PayerFeesCustoms = PayerFeesCustomsDictionary[invoice.InvoiceInformation[EInvoiceInformationControlNames.customsFeePayer]];
      }

      methodParams.Places = invoice.OptionsSeat.map((option) => {
        return {
          ActualWeight: option.weight,
          Width: option.volumetricWidth,
          Length: option.volumetricLength,
          Height: option.volumetricHeight,
          VolumetricWeight: option.volumetricVolume,
        };
      });
      if (invoice.ParcelPlaces) {
        methodParams.Invoices = invoice.ParcelPlaces.map((place, i) => {
          const Note = getGoodsClassifierDescription(place.description).trim();
          return {
            Note,
            CodeUKTZED: trimCodeUKTZEDByRecipientCountry(place[EParcelPlacesFormControlNames.codeUKTZED], invoice.AddressRecipient.country, this.userTypeService.isLoyaltyRozniza(), true),
            CodeUKTZEDDest: padCodeUKTZEDDestByCountry(place[EParcelPlacesFormControlNames.codeUKTZEDDest], invoice.AddressRecipient.country, this.userTypeService.isLoyaltyRozniza(), true),
            Material: place.materials,
            Country: place.country.Ref,
            Cost: place.cost,
            Currency: place.currency.Code,
            MeasurementsUnit: place.units.Ref,
            Amount: place.amount,
            Weight: place.weight,
            ...(place.brandModel ? {
              ManufacturerModel: place.brandModel,
              ManufacturerModelTranslation: invoice.translits.brandModel[i] || place.brandModel,
            } : {}),
          };
        });
      }
      if (invoice.InvoiceCheckbox === true) {
        methodParams.ClientInvoice = '1';
      } else {
        methodParams.ClientInvoice = '0';
      }
      invoice.DocumentUploader?.forEach((doc) => {
        if (doc.name !== '') {
          methodParams.Files = invoice.DocumentUploader.map((document) => {
            return {
              string: document.string,
              extension: document.extension,
            };
          });
        }
      });
      methodParams.Currency = invoice.InvoiceInformation.currency.Ref;
      methodParams.Incoterm = invoice.InvoiceInformation.conditions.Ref;
      methodParams.Cost = invoice.InvoiceInformation.totalCost;

      if (invoice.name) {
        methodParams.Name = invoice.name;
      }

      return methodParams;
    } catch (e) {
      console.error('Error in mapFormToApi', e);
      return {};
    }
  }
}
